import React from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  InputGroup,
  Label,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
} from "reactstrap";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { sessionEnded } from "../user/SessionEnded";
import Api from "../../components/helpers/Api";
import SeoNoIndex from "../../components/helpers/SeoNoIndex";

const REASON_LABELS = {
  NORMALIZED_TITLE_EXACT_MATCH: "Normalized title exact match",
  CROSS_TITLE_ALTERNATIVE_MATCH: "Title / alternative title cross-match",
  AUTHOR_SET_EXACT_MATCH: "Author set exact match",
  AUTHOR_SET_PARTIAL_MATCH: "Author set partial match",
  DIFFERENT_SONGBOOKS: "Found in different songbooks",
  TITLE_TOKEN_OVERLAP: "High title token similarity",
};

class AdminSongDuplicates extends React.Component {
  state = {
    isLoading: false,
    groups: [],
    totalPages: 0,
    currentPage: 0,
    searchQuery: "",
    confidenceFilter: "HIGH,MEDIUM",
    summary: null,
    unauthorized: false,
    isRefreshing: false,
  };

  componentDidMount() {
    try {
      const customer = sessionStorage.getItem("customer");
      const parsed = customer ? JSON.parse(customer) : null;
      if (parsed?.user?.isAdmin) {
        this.loadSummary();
        this.loadPage(0);
      } else {
        this.setState({ unauthorized: true });
      }
    } catch (e) {
      this.setState({ unauthorized: true });
    }
  }

  loadPage = (pageNumber) => {
    const { confidenceFilter, searchQuery } = this.state;
    const params = new URLSearchParams({
      page: pageNumber,
      size: 20,
      confidence: confidenceFilter,
    });

    if (searchQuery?.trim()) {
      params.append("query", searchQuery.trim());
    }

    this.setState({ isLoading: true });
    Api.get(`/api/admin/song-duplicates?${params.toString()}`)
      .then(response => {
        this.setState({
          groups: response.data.content || [],
          totalPages: response.data.totalPages || 0,
          currentPage: pageNumber,
          isLoading: false,
        });
      })
      .catch(err => {
        console.error("Error fetching duplicate song groups:", err);
        this.setState({ isLoading: false });
      });
  };

  loadSummary = () => {
    Api.get("/api/admin/song-duplicates/summary")
      .then(response => {
        this.setState({ summary: response.data });
      })
      .catch(err => {
        console.error("Error fetching duplicate song summary:", err);
      });
  };

  handleSearchSubmit = (e) => {
    e.preventDefault();
    this.loadPage(0);
  };

  handleClearSearch = () => {
    this.setState({ searchQuery: "" }, () => {
      this.loadPage(0);
    });
  };

  handleConfidenceChange = (value) => {
    this.setState({ confidenceFilter: value }, () => {
      this.loadPage(0);
    });
  };

  handleRefresh = () => {
    this.setState({ isRefreshing: true });
    Api.post("/api/admin/song-duplicates/refresh")
      .then(() => {
        this.loadSummary();
        this.loadPage(0);
      })
      .catch(err => {
        console.error("Error refreshing duplicate song cache:", err);
      })
      .finally(() => {
        this.setState({ isRefreshing: false });
      });
  };

  renderPagination = () => {
    const { currentPage, totalPages } = this.state;
    if (totalPages <= 1) {
      return null;
    }

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(0, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = startPage + maxVisiblePages - 1;
    if (endPage > totalPages - 1) {
      endPage = totalPages - 1;
      startPage = Math.max(0, endPage - (maxVisiblePages - 1));
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <PaginationItem active={i === currentPage} key={i}>
          <PaginationLink onClick={() => this.loadPage(i)}>
            {i + 1}
          </PaginationLink>
        </PaginationItem>
      );
    }

    return (
      <Pagination aria-label="Duplicate songs pagination" className="justify-content-center mt-4 flex-wrap">
        <PaginationItem disabled={currentPage === 0}>
          <PaginationLink previous onClick={() => this.loadPage(currentPage - 1)} />
        </PaginationItem>

        {startPage > 0 && (
          <>
            <PaginationItem>
              <PaginationLink onClick={() => this.loadPage(0)}>1</PaginationLink>
            </PaginationItem>
            <PaginationItem disabled>
              <PaginationLink>...</PaginationLink>
            </PaginationItem>
          </>
        )}

        {pages}

        {endPage < totalPages - 1 && (
          <>
            <PaginationItem disabled>
              <PaginationLink>...</PaginationLink>
            </PaginationItem>
            <PaginationItem>
              <PaginationLink onClick={() => this.loadPage(totalPages - 1)}>
                {totalPages}
              </PaginationLink>
            </PaginationItem>
          </>
        )}

        <PaginationItem disabled={currentPage === totalPages - 1}>
          <PaginationLink next onClick={() => this.loadPage(currentPage + 1)} />
        </PaginationItem>
      </Pagination>
    );
  };

  formatReason = (reason) => REASON_LABELS[reason] || reason;

  confidenceColor = (confidence) => {
    if (confidence === "HIGH") {
      return "danger";
    }
    if (confidence === "MEDIUM") {
      return "warning";
    }
    return "secondary";
  };

  renderSummaryBar = () => {
    const { summary, isRefreshing } = this.state;
    if (!summary) {
      return null;
    }

    return (
      <div className="d-flex flex-wrap align-items-center mb-3">
        <div className="small text-muted">
          <span>HIGH: <strong>{summary.highCount}</strong></span>
          <span className="mx-2">.</span>
          <span>MEDIUM: <strong>{summary.mediumCount}</strong></span>
          <span className="mx-2">.</span>
          <span>LOW: <strong>{summary.lowCount}</strong></span>
          <span className="mx-2">.</span>
          <span>Total: <strong>{summary.totalCount}</strong></span>
        </div>
        <div className="ml-auto small">
          <Button
            color="link"
            size="sm"
            className="p-0"
            disabled={isRefreshing}
            onClick={this.handleRefresh}
          >
            {isRefreshing ? "Refreshing..." : "Refresh"}
          </Button>
          <small className="text-muted ml-2">Cached &middot; refreshes every 10 min</small>
        </div>
      </div>
    );
  };

  renderFilters = () => {
    const { confidenceFilter, searchQuery } = this.state;
    return (
      <Form inline onSubmit={this.handleSearchSubmit} className="mb-3">
        <InputGroup>
          <Input
            type="text"
            placeholder="Filter by song title..."
            value={searchQuery}
            onChange={e => this.setState({ searchQuery: e.target.value })}
          />
          <Button color="primary" type="submit">Search</Button>
          <Button
            color="secondary"
            type="button"
            onClick={this.handleClearSearch}
            disabled={!searchQuery.trim()}
            className="ml-2"
          >
            Clear
          </Button>
        </InputGroup>

        <FormGroup check inline className="ml-3">
          <Label>Show:</Label>
        </FormGroup>
        <FormGroup check inline className="ml-2">
          <Label check>
            <Input
              type="radio"
              name="confidence"
              value="HIGH"
              checked={confidenceFilter === "HIGH"}
              onChange={() => this.handleConfidenceChange("HIGH")}
            /> HIGH only
          </Label>
        </FormGroup>
        <FormGroup check inline className="ml-2">
          <Label check>
            <Input
              type="radio"
              name="confidence"
              value="HIGH,MEDIUM"
              checked={confidenceFilter === "HIGH,MEDIUM"}
              onChange={() => this.handleConfidenceChange("HIGH,MEDIUM")}
            /> HIGH + MEDIUM
          </Label>
        </FormGroup>
        <FormGroup check inline className="ml-2">
          <Label check>
            <Input
              type="radio"
              name="confidence"
              value="HIGH,MEDIUM,LOW"
              checked={confidenceFilter === "HIGH,MEDIUM,LOW"}
              onChange={() => this.handleConfidenceChange("HIGH,MEDIUM,LOW")}
            /> All
          </Label>
        </FormGroup>
      </Form>
    );
  };

  renderGroup = (group, index) => {
    const { currentPage } = this.state;
    return (
      <div className="mb-4 border rounded p-3" key={group.groupId}>
        <div className="d-flex flex-wrap align-items-center mb-2">
          <strong>Duplicate Group #{index + 1 + (currentPage * 20)}</strong>
          <div className="ml-auto">
            <Badge color={this.confidenceColor(group.confidence)} className="mr-2">
              {group.confidence}
            </Badge>
            <span className="small text-muted">Score: {group.score}</span>
          </div>
        </div>

        <div className="small text-muted mb-2">
          <strong>Reasons:</strong>
          <ul className="mb-0 pl-3">
            {(group.reasons || []).map(reason => (
              <li key={reason}>{this.formatReason(reason)}</li>
            ))}
          </ul>
        </div>

        <Row>
          {(group.songs || []).map(song => (
            <Col key={song.id} md="6" className="mb-3 mb-md-0">
              <div className="p-2 bg-light rounded">
                <div><strong>Title:</strong> {song.title}</div>
                <div>
                  <strong>Alt:</strong>{" "}
                  {song.alternativeTitle || <em className="text-muted">none</em>}
                </div>
                <div>
                  <strong>Authors:</strong>{" "}
                  {song.authors?.length ? song.authors.join(", ") : <em className="text-muted">none</em>}
                </div>
                <div>
                  <strong>Songbooks:</strong>{" "}
                  {song.songbooks?.length ? song.songbooks.join(", ") : <em className="text-muted">none</em>}
                </div>
                <div className="small text-muted">ID: {song.id}</div>
                <Button
                  color="link"
                  size="sm"
                  className="p-0 mt-1"
                  onClick={() => this.props.navigate(song.adminLink)}
                >
                  View Song &rarr;
                </Button>
              </div>
            </Col>
          ))}
        </Row>
      </div>
    );
  };

  render() {
    if (!sessionStorage.getItem("customer")) {
      return sessionEnded();
    }
    if (this.state.unauthorized) {
      return <Navigate to="/" />;
    }

    const { groups, isLoading } = this.state;

    return (
      <>
        <SeoNoIndex noindex={true} />

        <div className="position-relative">
          <section className="section section-lg section-shaped pb-250">
            <div className="shape shape-style-1 shape-default">
              <span /><span /><span /><span /><span /><span /><span /><span /><span />
            </div>
            <Container className="py-lg-md d-flex">
              <div className="col px-0">
                <Row>
                  <Col lg="8">
                    <h1 className="display-3 text-white">
                      Dataset Quality <span>duplicate songs</span>
                    </h1>
                  </Col>
                </Row>
              </div>
            </Container>
            <div className="separator separator-bottom separator-skew">
              <svg xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" viewBox="0 0 2560 100">
                <polygon className="fill-white" points="2560 0 2560 100 0 100" />
              </svg>
            </div>
          </section>

          <section className="section section-lg pt-lg-0 mt--200">
            <Container>
              <Row className="justify-content-center">
                <Col lg="12">
                  <Row className="row-grid">
                    <Col lg="12">
                      <Card className="shadow border-0">
                        <CardBody className="py-5">
                          <Button color="link" onClick={() => this.props.navigate("/admin")} className="mb-3 p-0">
                            &larr; Back to menu
                          </Button>

                          {this.renderSummaryBar()}
                          {this.renderFilters()}

                          {isLoading ? (
                            <div>Loading...</div>
                          ) : (
                            <>
                              {groups.length > 0 ? (
                                groups.map((group, index) => this.renderGroup(group, index))
                              ) : (
                                <div className="text-center text-muted py-4">
                                  No duplicate groups found with the current filters.
                                </div>
                              )}
                            </>
                          )}

                          {this.renderPagination()}
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                  <Row>&nbsp;</Row>
                </Col>
              </Row>
            </Container>
          </section>
        </div>
      </>
    );
  }
}

function withRouterParamsAndNavigate(Component) {
  return function Wrapped(props) {
    const navigate = useNavigate();
    const params = useParams();
    return <Component {...props} navigate={navigate} params={params} />;
  };
}

export default withRouterParamsAndNavigate(AdminSongDuplicates);
